import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import LanguageSwitcher from "./LanguageSwitcher";
import NotificationBell from "./NotificationBell";
import {
  User, Briefcase, FileText, CalendarDays, MessageCircle,
  FolderClosed, Settings, LogOut, Sparkles,
} from "lucide-react";

export default function CandidateSidebar() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [matchCount, setMatchCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/missions/match-count");
        if (!cancelled) setMatchCount(data?.count ?? 0);
      } catch {
        if (!cancelled) setMatchCount(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const avatarSrc = user?.avatar
    ? (user.avatar.startsWith("http") ? user.avatar : `${process.env.REACT_APP_BACKEND_URL}${user.avatar}`)
    : null;

  const displayName = user?.name || [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "—";
  const initial = (user?.first_name?.[0] || user?.name?.[0] || "?").toUpperCase();

  // Completion percentage (mirrors logic in MyProfile)
  const completion = (() => {
    if (!user) return 0;
    const checks = [
      !!user.avatar, !!user.first_name, !!user.last_name, !!user.phone, !!user.email,
      !!user.address, !!user.postal_code, !!user.city, !!user.professional_role,
      !!user.recent_experience_dates,
      Array.isArray(user.softwares) && user.softwares.length > 0,
      Array.isArray(user.new_missions) && user.new_missions.length > 0,
      Array.isArray(user.available_weekdays) && user.available_weekdays.length > 0,
      Array.isArray(user.available_periods) && user.available_periods.length > 0,
      !!user.search_type, !!user.availability_type, !!user.max_distance_km, !!user.housing_preference,
    ];
    return Math.round(checks.filter(Boolean).length / checks.length * 100);
  })();

  const items = [
    { to: "/profile", label: t("csb.my_profile"), icon: User, testid: "csb-profile", match: (p) => p === "/profile" || p.startsWith("/profile/") },
    { to: "/availabilities", label: t("csb.my_availabilities"), icon: CalendarDays, testid: "csb-availabilities" },
    { to: "/missions", label: t("csb.proposed_missions"), icon: Briefcase, testid: "csb-missions" },
    { to: "/messages", label: t("nav.messages"), icon: MessageCircle, testid: "csb-messages" },
    { to: "/documents", label: t("csb.my_documents"), icon: FolderClosed, testid: "csb-documents" },
    { to: "/preferences", label: t("nav.settings"), icon: Settings, testid: "csb-settings" },
  ];

  return (
    <aside className="hidden lg:flex w-72 shrink-0 bg-white border-r border-[#E2E5E0] flex-col h-screen sticky top-0" data-testid="candidate-sidebar">
      {/* Logo */}
      <div className="px-6 pt-6 pb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <img src="/apocia-logo.png" alt="Apocia" className="h-10 w-auto" data-testid="candidate-sidebar-logo" />
        </div>
        <NotificationBell testid="csb-notif-bell" />
      </div>

      {/* User card */}
      <div className="px-6 py-5 border-b border-[#E2E5E0]">
        <div className="text-xs text-[#6B726A]">{t("auth.hello")}</div>
        <div className="text-base font-medium text-[#0F2A1B] mt-0.5 truncate" style={{ fontFamily: "Outfit" }} data-testid="csb-name">
          {displayName}
        </div>
        <div className="text-xs text-[#6B726A] mt-0.5 truncate">{user?.professional_role || t("csb.candidate")}</div>
        <div className="mt-4 flex justify-center">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-[#E8F0EA] grid place-items-center ring-4 ring-white shadow border border-[#E2E5E0]">
            {avatarSrc ? (
              <img src={avatarSrc} alt={displayName} className="w-full h-full object-cover" data-testid="csb-avatar" />
            ) : (
              <span className="text-2xl font-semibold text-[#1A4D2E]">{initial}</span>
            )}
          </div>
        </div>
        {/* Completion */}
        <div className="mt-4" data-testid="csb-completion">
          <div className="flex items-center justify-between text-[11px] mb-1">
            <span className="text-[#6B726A] uppercase tracking-wider font-medium">{t("nav.profile")}</span>
            <span className="font-medium text-[#1A4D2E]">{completion}%</span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-[#F0F2EF] overflow-hidden">
            <div className="h-full bg-[#1A4D2E] transition-all" style={{ width: `${completion}%` }} />
          </div>
        </div>

        {/* AI Boost badge */}
        {matchCount != null && matchCount > 0 && (
          <button
            onClick={() => nav("/missions")}
            data-testid="csb-ai-boost"
            className="mt-4 w-full text-left p-3 rounded-xl bg-gradient-to-r from-[#1A4D2E] to-[#225f3a] text-white hover:brightness-110 transition-all group"
          >
            <div className="flex items-center gap-2 mb-0.5">
              <Sparkles className="w-3.5 h-3.5 text-[#FFD46B]" />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-white/80">AI Boost</span>
            </div>
            <div className="text-xs leading-snug" data-testid="csb-match-count">
              {matchCount > 1 ? t("csb.missions_for_you", { count: matchCount }) : t("csb.mission_for_you", { count: matchCount })}
            </div>
            <div className="text-[10px] text-white/70 mt-0.5 group-hover:text-white/90">{t("csb.see_matches")}</div>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {items.map(({ to, label, icon: Icon, testid, match }) => {
          const active = match ? match(location.pathname) : location.pathname === to;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-colors ${
                active
                  ? "bg-[#E8F0EA] text-[#1A4D2E] font-medium border border-[#1A4D2E]/15"
                  : "text-[#3F4640] hover:bg-[#F7F8F6]"
              }`}
            >
              <Icon className="w-4 h-4" />
              <span className="truncate">{label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* Footer: lang + logout */}
      <div className="p-4 border-t border-[#E2E5E0] space-y-2">
        <LanguageSwitcher />
        <button
          onClick={async () => { await logout(); nav("/login"); }}
          data-testid="csb-logout"
          className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-sm text-[#E3655B] hover:bg-[#FFF4F3] transition-colors"
        >
          <LogOut className="w-4 h-4" /> {t("csb.logout")}
        </button>
      </div>
    </aside>
  );
}
